import { useLiveQuery } from 'dexie-react-hooks';

import { settings } from '@/lib/db';

/**
 * When a backup was last taken, and whether it's overdue —
 * `ARCHITECTURE.md` §7: "Backup reminder after 30 days without one."
 *
 * A key of its own (`lib/db`'s repository directly, not the Settings
 * feature's `useSetting`) because this is the backup subsystem's own
 * bookkeeping, not a preference a pilot sets on the Settings page — nothing
 * about it belongs to that feature, and reading and writing it here keeps
 * `features/backup` and `features/settings` from needing to know about each
 * other at all.
 */
const LAST_BACKUP_SETTING_KEY = 'backup.lastBackupAt';

export const BACKUP_REMINDER_DAYS = 30;

/**
 * Where a "Back up" link goes: the Backup panel itself, not the top of
 * Settings, where it sits below units, sources and the default aircraft and
 * a phone shows none of it. Spread into a `Link`: `<Link {...BACKUP_LINK}>`.
 */
export const BACKUP_HASH = 'backup';
export const BACKUP_LINK = { to: '/settings', hash: BACKUP_HASH } as const;

export async function recordBackupTaken(): Promise<void> {
  await settings.set(LAST_BACKUP_SETTING_KEY, new Date().toISOString());
}

/** `undefined` while loading, `null` if no backup has ever been taken. */
export function useLastBackupAt(): string | null | undefined {
  return useLiveQuery(
    () => settings.get<string | null>(LAST_BACKUP_SETTING_KEY, null),
    [],
    undefined,
  );
}

/** Days after the first import before "No backup taken yet" appears. */
export const FIRST_BACKUP_GRACE_DAYS = 7;

export function isBackupOverdue(
  lastBackupAt: string | null,
  thresholdDays: number = BACKUP_REMINDER_DAYS,
): boolean {
  if (lastBackupAt === null) {
    return true;
  }
  const ms = Date.now() - Date.parse(lastBackupAt);
  const days = ms / (24 * 60 * 60 * 1000);
  return days >= thresholdDays;
}
